import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { monthLabel } from '../lib/eventMonths';

/**
 * Backwards, forwards, and jump to a month — TROOP-123.
 *
 * **One control, both calendars.** It was written inside `ThreadList` for the conversations page's
 * events section; the unit's own calendar wants the same navigation, and "similar" is not good
 * enough for a control somebody learns once. Extracted rather than copied for the reason the
 * windowing rule behind it was: two month bars would part company the first time either was
 * touched.
 *
 * **Both steps move over months that actually hold something**, because `index` is the server's
 * list of months with events in them. Neither control can land somebody on an empty page, and the
 * picker cannot advertise a March that does not exist.
 *
 * **A control that cannot act says why** rather than going grey with no explanation — the rule
 * `Button` enforces through `disabledReason`. At the ends of a unit's history there is genuinely
 * nowhere to go, and "no earlier events" is the useful thing to say.
 */
export function MonthBar({
  index,
  shown,
  previous,
  next,
  busy,
  onAnchorChange,
}: {
  /** Every month with something in it, for the picker and the arrows. */
  index: { month: string; count: number }[];
  /** The months currently on screen, in order — the window, for the span label. */
  shown: { month: string; label: string }[];
  previous: string | null;
  next: string | null;
  busy: boolean;
  onAnchorChange: (month: string) => void;
}) {
  const span =
    shown.length === 0
      ? ''
      : shown.length === 1
        ? shown[0].label
        : `${shown[0].label} – ${shown[shown.length - 1].label}`;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Step
        label="Earlier events"
        onClick={() => previous && onAnchorChange(previous)}
        target={previous}
        busy={busy}
      >
        <ChevronLeft size={16} aria-hidden="true" />
      </Step>

      <Step
        label="Later events"
        onClick={() => next && onAnchorChange(next)}
        target={next}
        busy={busy}
      >
        <ChevronRight size={16} aria-hidden="true" />
      </Step>

      <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
        {busy ? 'Loading…' : span}
      </span>

      <label className="flex items-center gap-1" style={{ marginLeft: 'auto' }}>
        <span className="sr-only">Jump to a month</span>
        <select
          value={shown[0]?.month ?? ''}
          onChange={(e) => onAnchorChange(e.target.value)}
          className="t-caption"
          style={{
            background: 'var(--color-surface-raised)',
            border: '1px solid var(--color-border-strong)',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--space-1) var(--space-2)',
            minHeight: 'var(--tap-min)',
            color: 'var(--color-text-primary)',
          }}
        >
          {/* Only months that hold something. A picker offering an empty March would be offering a
              dead end, and one offering a month whose events are all in threads this reader cannot
              open would be telling them those events exist. */}
          {index.map((m) => (
            <option key={m.month} value={m.month}>
              {monthLabel(m.month)} ({m.count})
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function Step({
  label,
  target,
  busy,
  onClick,
  children,
}: {
  label: string;
  target: string | null;
  busy: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  const reason = target === null ? `No ${label.toLowerCase()}` : busy ? 'Loading' : undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={reason !== undefined}
      aria-label={reason ?? label}
      title={reason ?? label}
      className="inline-flex items-center justify-center"
      style={{
        background: 'var(--color-surface-raised)',
        border: '1px solid var(--color-border-strong)',
        borderRadius: 'var(--radius-sm)',
        minHeight: 'var(--tap-min)',
        minWidth: 'var(--tap-min)',
        color: reason !== undefined ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
        cursor: reason !== undefined ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/**
 * One month of a calendar, with what it draws bounded — TROOP-13.
 *
 * **The window cannot truncate; the screen can, as long as it says so.** A month arrives whole
 * from the server on purpose: cutting one at row 25 is the silent truncation this whole change
 * exists to remove. But drawing every row of it is the other bug the dev database keeps
 * demonstrating — one month of the dev troop is 113 events and 36,693px of page, and `/family` has
 * been here before at 4,165 buttons. So the month is complete in the payload, bounded on screen,
 * and the count of what is not drawn is on the control that reveals it.
 *
 * Shared by both calendars so the bound and the wording are one thing rather than two. Generic
 * over the row, because the member's page and the parent's draw different ones.
 */
export function EventMonthSection<T>({
  label,
  items,
  renderItem,
}: {
  label: string;
  items: T[];
  renderItem: (item: T) => ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);

  const drawn = expanded ? items : items.slice(0, EVENTS_DRAWN_PER_MONTH);
  const hidden = items.length - drawn.length;

  return (
    <section>
      <h3
        className="t-label"
        style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}
      >
        {label}
        {items.length > EVENTS_DRAWN_PER_MONTH && (
          <span className="tabular" style={{ fontWeight: 400 }}>
            {' '}
            &middot; {items.length}
          </span>
        )}
      </h3>

      <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
        {drawn.map((item) => renderItem(item))}
      </div>

      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="t-caption text-left"
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-secondary)',
            padding: 'var(--space-2) var(--space-1)',
            minHeight: 'var(--tap-min)',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Show {hidden} more in {label}
        </button>
      )}
    </section>
  );
}

/**
 * How many of a month's events are drawn before the rest go behind a tap.
 *
 * Chosen so a busy real month is never bounded at all -- a troop running something every week plus
 * a campout is nowhere near this. It exists for the month that is genuinely enormous, which on the
 * dev database is every month.
 */
const EVENTS_DRAWN_PER_MONTH = 24;
